/** Mobile navigation owns its state; the document remains the page scroller. */
(() => {
  if (customElements.get('slowform-navigation')) return;

  const INK = { dark: [98, 83, 75], light: [245, 245, 240] };
  const SAMPLE_INTERVAL = 240;

  function colour(value) {
    const parts = value?.match(/[\d.]+/g)?.map(Number);
    return parts?.length >= 3 ? [...parts.slice(0, 3), parts[3] ?? 1] : null;
  }

  function composite(front, back) {
    const alpha = front[3] ?? 1;
    return front.slice(0, 3).map((channel, index) => channel * alpha + back[index] * (1 - alpha));
  }

  function luminance(rgb) {
    return rgb.reduce((sum, channel, index) => {
      const value = channel / 255;
      return sum + (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4)
        * [0.2126, 0.7152, 0.0722][index];
    }, 0);
  }

  function contrast(a, b) {
    const first = luminance(a);
    const second = luminance(b);
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
  }

  class SlowformNavigation extends HTMLElement {
    #events;
    #observer;
    #surfaceObserver;
    #frame = 0;
    #mediaTimer = 0;
    #lastSample = 0;
    #lastY = 0;
    #travel = 0;
    #direction = 0;
    #keyboardFocus = false;
    #menu;
    #trigger;
    #opening = 0;
    #inertElements = [];
    #desktopItems = [];
    #desktopPointerStart = null;
    #suppressDesktopFocus = false;
    #canvas = document.createElement('canvas');
    #unreadableMedia = new WeakSet();
    #anonymousImages = new Map();

    connectedCallback() {
      if (this.#events) return;
      this.#events = new AbortController();
      this.dataset.input = 'pointer';
      const options = { signal: this.#events.signal };
      this.#trigger = this.querySelector('[data-sf-menu-trigger]');
      this.#menu = document.getElementById(this.#trigger?.getAttribute('aria-controls'));
      this.#lastY = Math.max(0, window.scrollY);
      this.#canvas.width = this.#canvas.height = 1;

      this.addEventListener('click', this.#click, options);
      this.addEventListener('focusin', this.#reveal, options);
      this.addEventListener('focusout', this.#schedule, options);
      window.addEventListener('scroll', this.#schedule, { ...options, passive: true });
      window.addEventListener('resize', this.#resize, { ...options, passive: true });
      window.visualViewport?.addEventListener('resize', this.#resize, { ...options, passive: true });
      window.visualViewport?.addEventListener('scroll', this.#schedule, { ...options, passive: true });
      window.addEventListener('pageshow', this.#reset, options);
      window.addEventListener('pagehide', this.#reset, options);
      document.addEventListener('visibilitychange', this.#visibility, options);
      document.addEventListener('keydown', this.#keyboard, options);
      document.addEventListener('pointerdown', this.#pointer, { ...options, passive: true });

      this.#desktopItems = [...this.querySelectorAll('.sf-nav-item')]
        .filter((item) => item.querySelector('[data-sf-desktop-trigger]'));
      for (const item of this.#desktopItems) {
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch') this.#setDesktopItem(item, true);
        }, options);
        item.addEventListener('pointerleave', (event) => {
          // A finger leaves after each tap, and when Safari hands a swipe to scrolling.
          // Touch menus stay open until a link, another menu, or the page is tapped.
          if (event.pointerType === 'touch') return;
          if (!this.#keyboardFocus || !item.contains(document.activeElement)) this.#setDesktopItem(item, false);
        }, options);
        item.addEventListener('focusin', () => {
          if (!this.#suppressDesktopFocus) this.#setDesktopItem(item, true);
        }, options);
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.#setDesktopItem(item, false);
        }, options);
      }
      this.#closeDesktopItems();

      if (this.#menu instanceof HTMLDialogElement) {
        if (!this.contains(this.#menu)) this.#menu.addEventListener('click', this.#click, options);
        this.#menu.addEventListener('cancel', this.#cancel, options);
        this.#menu.addEventListener('close', this.#menuClosed, options);
      }

      // Cart and quick-add may open programmatically; a second overlay replaces this menu.
      this.#observer = new MutationObserver((records) => {
        if (this.#menu?.open && records.some(({ target }) =>
          target instanceof HTMLDialogElement && target !== this.#menu && target.open)) {
          this.#closeMenu(false);
        }
        if (records.some(({ target }) => target instanceof HTMLDialogElement && target.open)) this.#closeDesktopItems();
        if (records.some(({ target }) => target.matches?.('.sf-localization__trigger'))) this.#schedule();
      });
      this.#observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded'] });
      this.#surfaceObserver = new ResizeObserver(this.#schedule);
      for (const panel of this.querySelectorAll('.sf-nav-flyout, .sf-localization__panel, [data-sf-brand]')) this.#surfaceObserver.observe(panel);
      this.#resize();
      this.#visibility();
    }

    disconnectedCallback() {
      this.#closeMenu(false);
      this.#events?.abort();
      this.#events = null;
      this.#observer?.disconnect();
      this.#surfaceObserver?.disconnect();
      this.#anonymousImages.clear();
      cancelAnimationFrame(this.#frame);
      clearInterval(this.#mediaTimer);
      this.#frame = this.#mediaTimer = 0;
    }

    #schedule = () => {
      if (!this.#frame) this.#frame = requestAnimationFrame(this.#update);
    };

    #update = () => {
      this.#frame = 0;
      if (!this.isConnected) return;
      const y = Math.max(0, window.scrollY);
      const delta = y - this.#lastY;
      const direction = Math.sign(delta);
      if (direction && direction !== this.#direction) this.#travel = 0;
      if (direction) this.#direction = direction;
      this.#travel += delta;
      this.#lastY = y;
      const atTop = y <= 16;
      this.dataset.top = String(atTop);
      const focused = this.#keyboardFocus && this.contains(document.activeElement);
      const barHeight = this.querySelector('.sf-nav__bar')?.offsetHeight || 64;
      // A hovered dropdown must not hold the desktop header open while the page scrolls down.
      // Actual page movement is required, so opening or scrolling a dropdown still works normally.
      if (window.innerWidth >= 750 && delta > 0 && this.#travel >= 12 && y > barHeight && !focused) {
        if (this.dataset.desktopOpen === 'true') this.#closeDesktopItems();
        this.querySelector('.sf-nav__localization .sf-localization:has(.sf-localization__trigger[aria-expanded="true"])')?.hidePanel?.();
      }
      if (atTop || this.#menu?.open || this.dataset.desktopOpen === 'true' || focused) {
        this.dataset.mode = 'full';
      } else if (Math.abs(this.#travel) >= 12) {
        if (this.#travel < 0) this.dataset.mode = 'full';
        else if (y > barHeight) this.dataset.mode = 'minimal';
      }
      this.#syncDesktopSurface();
      this.#syncMobileSurface();
      this.#sampleTone();
    };

    #syncMobileSurface() {
      if (window.innerWidth >= 750) return;
      const glass = this.querySelector('.sf-nav__glass');
      const brand = this.querySelector('[data-sf-brand]');
      if (!glass || !brand) return;
      const rect = brand.getBoundingClientRect();
      const parent = glass.offsetParent;
      const origin = parent?.getBoundingClientRect();
      const top = origin ? origin.top + parent.clientTop : -window.scrollY;
      const left = origin ? origin.left + parent.clientLeft : -window.scrollX;
      // The logo stays fixed. Its glass paints in document coordinates, including
      // content above Safari's visible viewport, instead of a cropped fixed layer.
      for (const [property, value] of Object.entries({
        '--sf-mobile-glass-top': `${-96 - top}px`,
        '--sf-mobile-glass-left': `${rect.left - left}px`,
        '--sf-mobile-glass-width': `${rect.width}px`,
        '--sf-mobile-glass-height': `${rect.bottom + 96}px`,
      })) {
        if (glass.style.getPropertyValue(property) !== value) glass.style.setProperty(property, value);
      }
    }

    #syncDesktopSurface() {
      if (window.innerWidth < 750) return;
      const panel = this.querySelector('.sf-nav-item[data-open="true"] > .sf-nav-flyout')
        || this.querySelector('.sf-localization:has(.sf-localization__trigger[aria-expanded="true"]) .sf-localization__panel');
      const bar = this.querySelector('.sf-nav__bar').getBoundingClientRect();
      const rect = panel?.getBoundingClientRect();
      this.style.setProperty('--sf-flyout-height', `${rect ? Math.max(0, rect.bottom - bar.bottom) : 0}px`);
      this.style.setProperty('--sf-flyout-left', `${rect ? rect.left - bar.left : 0}px`);
      this.style.setProperty('--sf-flyout-right', `${rect ? rect.right - bar.left : bar.width}px`);
    }

    #resize = () => {
      const viewport = window.visualViewport;
      // Pinch zoom must not shrink the navigation's layout viewport.
      const height = !viewport || viewport.scale !== 1 ? window.innerHeight : viewport.height;
      this.style.setProperty('--sf-viewport-height', `${Math.round(height)}px`);
      if (this.#menu && !this.contains(this.#menu)) {
        this.#menu.style.setProperty('--sf-viewport-height', `${Math.round(height)}px`);
      }
      if (window.innerWidth >= 750) this.#closeMenu(false);
      else this.#closeDesktopItems();
      this.#lastSample = 0;
      this.#schedule();
    };

    #reveal = () => { this.dataset.mode = 'full'; this.#lastSample = 0; this.#schedule(); };
    #keyboard = (event) => {
      if (event.key === 'Tab') { this.#keyboardFocus = true; this.dataset.input = 'keyboard'; }
      if (this.#menu?.open) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.#closeMenu();
        } else if (event.key === 'Tab') {
          const controls = [...this.#menu.querySelectorAll('a[href], button, input, select, textarea, summary, [tabindex]')]
            .filter((element) => !element.disabled && element.tabIndex >= 0 && !element.closest('[inert]')
              && element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true }));
          event.preventDefault();
          if (!controls.length) return;
          const index = controls.indexOf(document.activeElement);
          const nextIndex = index < 0 ? (event.shiftKey ? controls.length - 1 : 0)
            : (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
          const next = controls[nextIndex];
          next.focus({ preventScroll: true });
          // Scroll only the menu's own link/list region when tabbing to hidden rows.
          for (let parent = next.parentElement; parent && this.#menu.contains(parent); parent = parent.parentElement) {
            if (!/(auto|scroll)/.test(getComputedStyle(parent).overflowY) || parent.scrollHeight <= parent.clientHeight) continue;
            const item = next.getBoundingClientRect(), region = parent.getBoundingClientRect();
            if (item.top < region.top) parent.scrollTop -= region.top - item.top;
            else if (item.bottom > region.bottom) parent.scrollTop += item.bottom - region.bottom;
          }
        }
        return;
      }
      if (event.key !== 'Escape' || this.dataset.desktopOpen !== 'true') return;
      const item = this.#desktopItems.find((candidate) => candidate.dataset.open === 'true');
      if (!item) return;
      event.preventDefault();
      this.#closeDesktopItems();
      this.#suppressDesktopFocus = true;
      item.querySelector('[data-sf-desktop-trigger]')?.focus({ preventScroll: true });
      this.#suppressDesktopFocus = false;
    };
    #pointer = (event) => {
      this.#keyboardFocus = false;
      this.dataset.input = 'pointer';
      const trigger = event.target instanceof Element ? event.target.closest('[data-sf-desktop-trigger]') : null;
      const item = trigger?.closest('.sf-nav-item');
      this.#desktopPointerStart = item && this.contains(item)
        ? { trigger, open: item.dataset.open === 'true', pointerType: event.pointerType } : null;
      if (!this.#desktopItems.some((candidate) => candidate.contains(event.target))) this.#closeDesktopItems();
    };

    #setDesktopItem(item, open) {
      if (open && window.innerWidth < 750) return;
      if (open) {
        for (const other of this.#desktopItems) {
          if (other !== item) {
            other.dataset.open = 'false';
            other.querySelector('[data-sf-desktop-trigger]')?.setAttribute('aria-expanded', 'false');
          }
        }
        this.dataset.mode = 'full';
      }
      item.dataset.open = String(open);
      item.querySelector('[data-sf-desktop-trigger]')?.setAttribute('aria-expanded', String(open));
      this.dataset.desktopOpen = String(this.#desktopItems.some((candidate) => candidate.dataset.open === 'true'));
      this.#lastSample = 0;
      this.#schedule();
    }

    #closeDesktopItems() {
      for (const item of this.#desktopItems) {
        item.dataset.open = 'false';
        item.querySelector('[data-sf-desktop-trigger]')?.setAttribute('aria-expanded', 'false');
      }
      this.dataset.desktopOpen = 'false';
      this.#lastSample = 0;
      this.#schedule();
    }

    #reset = (event) => {
      // Initial pageshow may arrive after the visitor has already scrolled.
      // Only a cache restoration or page departure should reset navigation.
      if (event?.type === 'pageshow' && !event.persisted) {
        this.#schedule();
        return;
      }
      this.#closeMenu(false);
      this.#closeDesktopItems();
      this.#travel = this.#direction = 0;
      this.#lastY = Math.max(0, window.scrollY);
      this.dataset.mode = 'full';
      this.#lastSample = 0;
      this.#resize();
    };

    #visibility = () => {
      clearInterval(this.#mediaTimer);
      this.#mediaTimer = 0;
      if (!document.hidden) {
        this.#lastSample = 0;
        this.#schedule();
        // Covers a playing hero without attaching listeners to or reloading media.
        this.#mediaTimer = window.setInterval(this.#schedule, SAMPLE_INTERVAL);
      }
    };

    #click = (event) => {
      if (!(event.target instanceof Element)) return;
      const desktopTrigger = event.target.closest('[data-sf-desktop-trigger]');
      if (desktopTrigger && window.innerWidth >= 750) {
        const item = desktopTrigger.closest('.sf-nav-item');
        if (!item) return;
        // A menu-only parent such as Discover must not jump the page to the top.
        if (desktopTrigger.getAttribute('href') === '#') event.preventDefault();
        // Pointerdown precedes focusin: remember whether this click began on a closed menu.
        const pointerStart = this.#desktopPointerStart?.trigger === desktopTrigger ? this.#desktopPointerStart : null;
        const wasOpen = pointerStart ? pointerStart.open : item.dataset.open === 'true';
        this.#desktopPointerStart = null;
        if (!wasOpen) {
          event.preventDefault();
          this.#setDesktopItem(item, true);
        } else if (pointerStart?.pointerType === 'touch' && desktopTrigger.getAttribute('href') === '#') {
          this.#setDesktopItem(item, false);
        }
      } else if (event.target.closest('[data-sf-menu-trigger]')) {
        event.preventDefault();
        this.#openMenu();
      } else if (event.target.closest('.sf-localization--mobile > summary')?.parentElement.open) {
        event.preventDefault();
        event.target.closest('.sf-localization--mobile').open = false;
        this.#menu.querySelector('[data-sf-menu-close]')?.focus({ preventScroll: true });
      } else if (event.target.closest('[data-sf-menu-close]')) {
        this.#closeMenu();
      } else if (event.target.closest('[data-sf-menu-cart]')) {
        this.#closeMenu(false);
        const cart = this.querySelector('cart-drawer-component') || document.querySelector('cart-drawer-component');
        if (typeof cart?.open === 'function') cart.open();
        else document.querySelector('.header-actions__cart-button')?.click();
      } else if (this.#menu?.open && this.#menu.contains(event.target)) {
        if (!event.target.closest('.sf-nav-panel')) this.#closeMenu();
        else if (event.target.closest('a[href]')) this.#closeMenu(false);
      }
    };

    #openMenu = async () => {
      if (!(this.#menu instanceof HTMLDialogElement) || this.#menu.open || window.innerWidth >= 750) return;
      const opening = ++this.#opening;
      for (const dialog of document.querySelectorAll('dialog[open]')) {
        let owner = dialog.parentElement;
        while (owner && typeof owner.closeDialog !== 'function') owner = owner.parentElement;
        if (owner) await owner.closeDialog();
        else dialog.close();
      }
      if (!this.isConnected || opening !== this.#opening || window.innerWidth >= 750) return;
      this.dataset.mode = 'full';
      this.dataset.menuOpen = 'true';
      this.#trigger?.setAttribute('aria-expanded', 'true');
      // Non-modal dialog placement avoids Safari's automatic modal edge colour fill.
      // Inert siblings and the keyboard handler provide modal interaction semantics.
      this.#menu.setAttribute('aria-modal', 'true');
      this.#menu.style.setProperty('--sf-menu-top', '0px');
      this.#menu.open = true;
      const containingBlock = this.#menu.offsetParent;
      const origin = containingBlock ? containingBlock.getBoundingClientRect().top + containingBlock.clientTop : -window.scrollY;
      this.#menu.style.setProperty('--sf-menu-top', `${-origin}px`);
      for (let branch = this.#menu; branch && branch !== document.body; branch = branch.parentElement) {
        for (const sibling of branch.parentElement?.children || []) {
          if (sibling !== branch && !sibling.inert) {
            this.#inertElements.push(sibling);
            sibling.inert = true;
          }
        }
      }
      this.#menu.querySelector('[data-sf-menu-close]')?.focus({ preventScroll: true });
    };

    #closeMenu(restoreFocus = true) {
      this.#opening++;
      const wasOpen = this.#menu?.open;
      if (wasOpen) this.#menu.close();
      this.#restoreMenuBackground();
      this.dataset.menuOpen = 'false';
      this.#trigger?.setAttribute('aria-expanded', 'false');
      if (wasOpen && restoreFocus && this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: true });
      this.#lastSample = 0;
      this.#schedule();
    }

    #restoreMenuBackground() {
      for (const drawer of this.#menu?.querySelectorAll('.sf-localization--mobile[open]') || []) drawer.open = false;
      for (const element of this.#inertElements) element.inert = false;
      this.#inertElements = [];
      this.#menu?.removeAttribute('aria-modal');
    }

    #cancel = (event) => { event.preventDefault(); this.#closeMenu(); };
    #menuClosed = () => {
      if (this.#menu?.open) return;
      this.#restoreMenuBackground();
      this.dataset.menuOpen = 'false';
      this.#trigger?.setAttribute('aria-expanded', 'false');
      this.#schedule();
    };

    #sampleTone() {
      const now = performance.now();
      if (document.hidden || this.#menu?.open || now - this.#lastSample < SAMPLE_INTERVAL) return;
      this.#lastSample = now;
      const brand = this.querySelector('[data-sf-brand]');
      if (!brand) return;
      const rect = brand.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight || !rect.width) return;
      const y = Math.max(1, Math.min(window.innerHeight - 1, rect.top + rect.height / 2));
      const colours = [0.2, 0.5, 0.8].map((fraction) => this.#colourAt(rect.left + rect.width * fraction, y));
      const background = [0, 1, 2].map((channel) => colours.reduce((sum, rgb) => sum + rgb[channel], 0) / colours.length);
      const glass = Number.parseFloat(getComputedStyle(this).getPropertyValue('--sf-nav-glass-opacity'));
      const effective = composite([...INK.light, Number.isFinite(glass) ? glass : 0], background);
      const darkContrast = contrast(INK.dark, effective);
      const lightContrast = contrast(INK.light, effective);
      // A small margin prevents flicker around the crossover on moving imagery.
      const current = this.dataset.tone === 'light' ? 'light' : 'dark';
      const next = lightContrast > darkContrast ? 'light' : 'dark';
      if (next === current || Math.abs(lightContrast - darkContrast) > 0.35) this.dataset.tone = next;
    }

    #colourAt(x, y) {
      const elements = document.elementsFromPoint(x, y).filter((element) =>
        !this.contains(element) && !element.closest('dialog, slowform-navigation')
        && !(element !== document.body && element !== document.documentElement && element.contains(this)));
      const surface = elements.find((element) => element !== document.documentElement && element !== document.body);
      const hint = surface?.closest('[data-sf-header-tone]')?.dataset.sfHeaderTone;
      let background = INK.light;
      // Resolve the nearest actual page background rather than the header's own colour.
      for (const element of [...elements].reverse()) {
        const value = colour(getComputedStyle(element).backgroundColor);
        if (value?.[3]) background = composite(value, background);
      }
      let media = elements.find((element) => element instanceof HTMLImageElement || element instanceof HTMLVideoElement);
      if (!media) {
        // Decorative media often has pointer-events:none and is absent from hit testing.
        const section = surface?.closest('.shopify-section, section');
        media = section && [...section.querySelectorAll('video, img')].find((candidate) => {
          if (candidate.closest('dialog, slowform-navigation')) return false;
          const bounds = candidate.getBoundingClientRect();
          const style = getComputedStyle(candidate);
          return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom
            && style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) > 0;
        });
      }
      const pixel = media && this.#mediaPixel(media, x, y);
      if (pixel) return composite(pixel, background);
      // Canvas access can be blocked by the media host. Declared tones remain optional fallbacks.
      if (hint === 'light') return [35, 35, 35];
      if (hint === 'dark') return INK.light;
      if (media && this.dataset.home === 'true' && this.dataset.top === 'true') return [35, 35, 35];
      return background;
    }

    #anonymousImage(media) {
      if (!(media instanceof HTMLImageElement)) return null;
      const url = media.currentSrc || media.src;
      if (!/^https?:/i.test(url)) return null;
      let entry = this.#anonymousImages.get(url);
      if (!entry) {
        // Attempt each URL once, with a small hard cap on decoded image memory.
        // Further images use their declared/computed tone rather than growing the cache.
        if (this.#anonymousImages.size >= 6 || !this.#events) return null;
        const image = new Image();
        entry = { image, ready: false, failed: false };
        this.#anonymousImages.set(url, entry);
        image.crossOrigin = 'anonymous';
        image.decoding = 'async';
        image.referrerPolicy = media.referrerPolicy;
        image.addEventListener('load', () => {
          entry.ready = true;
          this.#lastSample = 0;
          this.#schedule();
        }, { once: true, signal: this.#events.signal });
        image.addEventListener('error', () => {
          entry.failed = true;
          entry.image = null;
        }, { once: true, signal: this.#events.signal });
        // This separate request never changes or reloads the displayed image/video.
        image.src = url;
      }
      return entry.ready && !entry.failed ? entry.image : null;
    }

    #mediaPixel(media, x, y) {
      const source = this.#unreadableMedia.has(media) ? this.#anonymousImage(media) : media;
      if (!source) return null;
      const width = source instanceof HTMLVideoElement ? source.videoWidth : source.naturalWidth;
      const height = source instanceof HTMLVideoElement ? source.videoHeight : source.naturalHeight;
      if (!width || !height || (media instanceof HTMLVideoElement && media.readyState < 2)) return null;
      const rect = media.getBoundingClientRect();
      const style = getComputedStyle(media);
      const fit = style.objectFit;
      const scale = fit === 'cover' ? Math.max(rect.width / width, rect.height / height)
        : fit === 'none' ? 1 : Math.min(rect.width / width, rect.height / height);
      const scaledWidth = fit === 'fill' ? rect.width : width * (fit === 'scale-down' ? Math.min(scale, 1) : scale);
      const scaledHeight = fit === 'fill' ? rect.height : height * (fit === 'scale-down' ? Math.min(scale, 1) : scale);
      const positions = style.objectPosition.split(' ');
      const offset = (position, space) => position?.endsWith('%') ? space * parseFloat(position) / 100
        : position?.endsWith('px') ? parseFloat(position) : space / 2;
      const sourceX = (x - rect.left - offset(positions[0], rect.width - scaledWidth)) * width / scaledWidth;
      const sourceY = (y - rect.top - offset(positions[1], rect.height - scaledHeight)) * height / scaledHeight;
      if (sourceX < 0 || sourceY < 0 || sourceX >= width || sourceY >= height) return null;
      try {
        const context = this.#canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return null;
        context.clearRect(0, 0, 1, 1);
        context.drawImage(source, Math.max(0, sourceX - 4), Math.max(0, sourceY - 4), Math.min(8, width), Math.min(8, height), 0, 0, 1, 1);
        const [r, g, b, alpha] = context.getImageData(0, 0, 1, 1).data;
        return [r, g, b, alpha / 255];
      } catch {
        this.#unreadableMedia.add(media);
        // Reset a canvas tainted by a cross-origin image before sampling another source.
        this.#canvas.width = 1;
        if (source === media) this.#anonymousImage(media);
        else {
          const entry = this.#anonymousImages.get(media.currentSrc || media.src);
          if (entry) { entry.failed = true; entry.image = null; }
        }
        return null;
      }
    }
  }

  customElements.define('slowform-navigation', SlowformNavigation);
})();
